import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import {
  Plus, Search, Pencil, Trash2, MessageSquare, DollarSign,
  Wrench, AlertCircle, CheckCircle, XCircle, Clock, Loader2,
  ChevronLeft, Building, Calendar, User, Tag, Hash,
  ClipboardList, SlidersHorizontal, BrainCircuit, HardHat, Phone, Mail,
  Activity, ImageIcon, X, ChevronRight, Bot
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';
import maintenanceService from '../services/maintenanceService';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { providerService } from '../services/providerService';

// ─── Helpers ───────────────────────────────────────────────────────

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'Emergency': return 'bg-red-100 text-red-800 border-red-200';
    case 'High':      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Medium':    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':       return 'bg-green-100 text-green-800 border-green-200';
    default:          return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusIcon = (status, cls = 'h-4 w-4') => {
  switch (status) {
    case 'Open':        return <AlertCircle className={cn(cls, 'text-red-500')} />;
    case 'In Progress': return <Clock className={cn(cls, 'text-blue-500')} />;
    case 'Resolved':    return <CheckCircle className={cn(cls, 'text-green-500')} />;
    case 'Closed':      return <CheckCircle className={cn(cls, 'text-gray-400')} />;
    default:            return <Wrench className={cn(cls, 'text-gray-400')} />;
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'Open':         return 'bg-red-50 text-red-700 border-red-200';
    case 'In Progress':  return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Pending Parts':return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'Scheduled':    return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    case 'Resolved':     return 'bg-green-50 text-green-700 border-green-200';
    case 'Closed':       return 'bg-gray-50 text-gray-600 border-gray-200';
    default:             return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)   return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: diffDays > 365 ? 'numeric' : undefined });
};

// ─── Left Panel: Request Card ──────────────────────────────────────

const RequestCard = ({ request, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full text-left px-4 py-3.5 border-b last:border-0 transition-colors',
      'hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50',
      isSelected
        ? 'bg-cyan-50 border-l-[3px] border-l-cyan-500 pl-[13px]'
        : 'border-l-[3px] border-l-transparent',
    )}
  >
    <div className="flex items-start justify-between gap-2 mb-0.5">
      <p className={cn(
        'text-sm font-medium truncate flex-1 leading-snug',
        isSelected ? 'text-cyan-800' : 'text-gray-800',
      )}>
        {request.title}
      </p>
      <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-0.5 whitespace-nowrap">
        {formatDate(request.createdAt)}
      </span>
    </div>

    {request.caseNumber && (
      <p className={cn(
        'text-[10px] font-mono font-semibold mb-1',
        isSelected ? 'text-cyan-600' : 'text-muted-foreground',
      )}>
        <span className="font-sans font-normal opacity-70">Case No. </span>{request.caseNumber}
      </p>
    )}

    <p className="text-xs text-muted-foreground truncate leading-relaxed mb-2">
      {request.description}
    </p>

    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {getStatusIcon(request.status, 'h-3 w-3')}
        <span>{request.status}</span>
      </div>
      <span className={cn(
        'inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold leading-4',
        getPriorityColor(request.priority),
      )}>
        {request.priority}
      </span>
      {request.property?.name && (
        <span className="text-[11px] text-muted-foreground truncate">{request.property.name}</span>
      )}
    </div>
  </button>
);

// ─── Right Panel: Detail Section ──────────────────────────────────

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="h-8 w-8 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-gray-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-gray-800 truncate">{value}</p>
      </div>
    </div>
  );
};

// ─── Provider Row (single-line compact) ───────────────────────────

const ProviderRow = ({ assignedTo }) => {
  if (!assignedTo?.name) return null;
  const parts = (assignedTo.contactInfo || '').split(' | ').filter(Boolean);
  const phone = parts.find(p => /^\(?\d/.test(p.trim()));
  const email = parts.find(p => p.includes('@'));
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="h-7 w-7 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
        <HardHat className="h-3.5 w-3.5 text-cyan-600" />
      </div>
      <span className="text-sm font-medium text-gray-800 truncate">{assignedTo.name}</span>
      {phone && (
        <a href={`tel:${phone.replace(/\s/g,'')}`}
          className="flex items-center gap-1 text-xs text-cyan-600 hover:underline flex-shrink-0">
          <Phone className="h-3 w-3" />{phone}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`}
          className="flex items-center gap-1 text-xs text-cyan-600 hover:underline flex-shrink-0">
          <Mail className="h-3 w-3" />{email}
        </a>
      )}
      {!phone && !email && assignedTo.contactInfo && (
        <span className="text-xs text-muted-foreground truncate">{assignedTo.contactInfo}</span>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────

const Maintenance = ({ user }) => {
  const isTenant = user?.userType === 'Tenant';
  const [searchParams] = useSearchParams();

  const [requests,   setRequests]   = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants,    setTenants]    = useState([]);
  const [providers,  setProviders]  = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Inline provider assignment state
  const [isAssignOpen,    setIsAssignOpen]    = useState(false);
  const [assignProviderId, setAssignProviderId] = useState('');

  const [searchQuery,    setSearchQuery]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const [selectedRequest,  setSelectedRequest]  = useState(null);
  const [loadingDetail,    setLoadingDetail]     = useState(false);

  // Mobile panel toggle
  const [mobilePanel, setMobilePanel] = useState('list');

  // Modals
  const [isAddOpen,    setIsAddOpen]    = useState(false);
  const [isEditOpen,   setIsEditOpen]   = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [modalTarget,  setModalTarget]  = useState(null); // request used for edit/delete modal

  const [formData, setFormData] = useState({
    propertyId: '', tenantId: '', unitNumber: '',
    title: '', description: '', category: 'General Maintenance', priority: 'Medium',
  });

  const [commentText, setCommentText] = useState('');
  const [costData, setCostData] = useState({ description: '', amount: '', category: 'Other' });

  // Lightbox for photo thumbnails
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Conversation dialog (owner only)
  const [convOpen, setConvOpen] = useState(false);
  const [convMessages, setConvMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState('');

  // Inline approval (landlord, Pending Approval cases)
  const [approvalReason,  setApprovalReason]  = useState('');
  const [approvalError,   setApprovalError]   = useState('');
  const [approvalResult,  setApprovalResult]  = useState(null); // 'approved' | 'rejected'

  const commentsEndRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  // Auto-select case when linked via ?case=<id> (e.g. from approval email)
  useEffect(() => {
    const caseId = searchParams.get('case');
    if (!caseId || !requests.length || selectedRequest) return;
    const match = requests.find(r => r._id === caseId);
    if (match) openDetail(match);
  }, [requests, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedRequest?.comments?.length]);

  const loadData = async () => {
    try {
      setLoading(true);
      const fetches = [
        maintenanceService.getAll(),
        propertyService.getAll(),
        isTenant ? Promise.resolve([]) : tenantService.getAllTenants(),
        isTenant ? Promise.resolve([]) : providerService.getAll({ status: 'active' }),
      ];
      const [requestsData, propertiesData, tenantsData, providersData] = await Promise.all(fetches);
      setRequests(requestsData);
      setProperties(propertiesData);
      setTenants(tenantsData);
      setProviders(providersData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (request) => {
    setMobilePanel('detail');
    setLoadingDetail(true);
    setSelectedRequest(null);
    setCommentText('');
    setCostData({ description: '', amount: '', category: 'Other' });
    setIsAssignOpen(false);
    setAssignProviderId('');
    setApprovalReason('');
    setApprovalError('');
    setApprovalResult(null);
    try {
      const full = await maintenanceService.getById(request._id);
      setSelectedRequest(full);
    } catch (err) {
      console.error('Failed to load request details:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openAdd = () => {
    setFormData({ propertyId: '', tenantId: '', unitNumber: '', title: '', description: '', category: 'General Maintenance', priority: 'Medium' });
    setFormError('');
    setIsAddOpen(true);
  };

  const openEdit = (request) => {
    setModalTarget(request);
    setIsAssignOpen(false);
    setFormData({
      propertyId:         request.property._id,
      tenantId:           request.tenant?._id || '',
      unitNumber:         request.unitNumber || '',
      title:              request.title,
      description:        request.description,
      category:           request.category,
      priority:           request.priority,
      status:             request.status,
      assignedToProviderId: '',
      assignedToManual:   request.assignedTo?.name || '',
      scheduledDate:      request.scheduledDate ? new Date(request.scheduledDate).toISOString().split('T')[0] : '',
      estimatedCost:      request.estimatedCost || 0,
      actualCost:         request.actualCost || 0,
    });
    setFormError('');
    setIsEditOpen(true);
  };

  const openDelete = (request) => {
    setModalTarget(request);
    setIsDeleteOpen(true);
  };

  const handleAssignProvider = async () => {
    const provider = providers.find(p => p._id === assignProviderId);
    if (!provider) return;
    setSubmitting(true);
    try {
      const contactParts = [
        provider.contact?.phone,
        provider.contact?.email,
      ].filter(Boolean);
      const assignedTo = {
        name: provider.company ? `${provider.name} · ${provider.company}` : provider.name,
        type: 'Contractor',
        contactInfo: contactParts.join(' | '),
      };
      await maintenanceService.update(selectedRequest._id, { assignedTo, providerEmail: provider.contact?.email });
      const full = await maintenanceService.getById(selectedRequest._id);
      setSelectedRequest(full);
      setRequests(prev => prev.map(r => r._id === full._id ? { ...r, assignedTo: full.assignedTo } : r));
      setIsAssignOpen(false);
      setAssignProviderId('');
    } catch (err) {
      console.error('Failed to assign provider:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineApproval = async (action) => {
    if (action === 'reject' && !approvalReason.trim()) {
      setApprovalError('Please provide a reason for rejection.');
      return;
    }
    setApprovalError('');
    setSubmitting(true);
    try {
      await maintenanceService.submitApproval(selectedRequest._id, action, approvalReason.trim() || undefined);
      setApprovalResult(action);
      const full = await maintenanceService.getById(selectedRequest._id);
      setSelectedRequest(full);
      setRequests(prev => prev.map(r => r._id === full._id ? { ...r, status: full.status, approvalStatus: full.approvalStatus } : r));
    } catch (err) {
      setApprovalError(err.response?.data?.error || 'Failed to submit approval action.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      await maintenanceService.create(formData);
      await loadData();
      setIsAddOpen(false);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create maintenance request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      let assignedTo;
      let providerEmail;
      if (formData.assignedToProviderId && formData.assignedToProviderId !== '__manual__') {
        const p = providers.find(pr => pr._id === formData.assignedToProviderId);
        if (p) {
          const contactParts = [p.contact?.phone, p.contact?.email].filter(Boolean);
          assignedTo = {
            name: p.company ? `${p.name} · ${p.company}` : p.name,
            type: 'Contractor',
            contactInfo: contactParts.join(' | '),
          };
          providerEmail = p.contact?.email;
        }
      } else if (formData.assignedToManual) {
        assignedTo = { name: formData.assignedToManual, type: 'Internal', contactInfo: '' };
      }
      const updated = await maintenanceService.update(modalTarget._id, {
        ...formData,
        assignedTo,
        ...(providerEmail ? { providerEmail } : {}),
      });
      await loadData();
      // Refresh the detail panel if the edited item is currently selected
      if (selectedRequest?._id === modalTarget._id) {
        const full = await maintenanceService.getById(modalTarget._id);
        setSelectedRequest(full);
      }
      setIsEditOpen(false);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update maintenance request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await maintenanceService.delete(modalTarget._id);
      if (selectedRequest?._id === modalTarget._id) {
        setSelectedRequest(null);
        setMobilePanel('list');
      }
      await loadData();
      setIsDeleteOpen(false);
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const updated = await maintenanceService.addComment(selectedRequest._id, commentText);
      setSelectedRequest(updated);
      setCommentText('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadConversation = async () => {
    setConvOpen(true);
    setConvLoading(true);
    setConvMessages([]);
    try {
      const data = await maintenanceService.getConversation(selectedRequest._id);
      setConvMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setConvLoading(false);
    }
  };

  const handleAddCost = async () => {
    if (!costData.amount) return;
    setSubmitting(true);
    try {
      const updated = await maintenanceService.addCost(selectedRequest._id, {
        ...costData,
        amount: parseFloat(costData.amount),
      });
      setSelectedRequest(updated);
      setCostData({ description: '', amount: '', category: 'Other' });
    } catch (err) {
      console.error('Failed to add cost:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const q = searchQuery.toLowerCase();
    const matchesSearch   = req.title.toLowerCase().includes(q) ||
                            req.description.toLowerCase().includes(q) ||
                            (req.caseNumber || '').toLowerCase().includes(q);
    const matchesStatus   = !statusFilter   || req.status   === statusFilter;
    const matchesPriority = !priorityFilter || req.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // ── Form shared between Add and Edit ────────────────────────────
  const renderMaintenanceForm = (onSubmit, submitLabel, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {formError && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded text-sm">{formError}</div>
      )}

      <div className="space-y-2">
        <Label htmlFor="propertyId">Property *</Label>
        <select id="propertyId" name="propertyId" value={formData.propertyId} onChange={handleInputChange} required disabled={submitting}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="">Select a property</option>
          {properties.map(p => <option key={p._id} value={p._id}>{p.name} - {p.address}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="unitNumber">Unit Number</Label>
        <Input id="unitNumber" name="unitNumber" value={formData.unitNumber} onChange={handleInputChange} disabled={submitting} placeholder="e.g., 101, 2B" />
      </div>

      {!isTenant && (
        <div className="space-y-2">
          <Label htmlFor="tenantId">Tenant (Optional)</Label>
          <select id="tenantId" name="tenantId" value={formData.tenantId} onChange={handleInputChange} disabled={submitting}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="">Select a tenant (optional)</option>
            {tenants.map(t => <option key={t._id} value={t._id}>{t.name} - Unit {t.unitNumber}</option>)}
          </select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input id="title" name="title" value={formData.title} onChange={handleInputChange} required disabled={submitting} placeholder="Brief description of the issue" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} required disabled={submitting} rows="4"
          placeholder="Detailed description of the maintenance issue"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <select id="category" name="category" value={formData.category} onChange={handleInputChange} required disabled={submitting}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            {['Plumbing','Electrical','HVAC','Appliance','Structural','Pest Control','Lock/Security','General Maintenance','Emergency','Other'].map(c =>
              <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="priority">Priority *</Label>
          <select id="priority" name="priority" value={formData.priority} onChange={handleInputChange} required disabled={submitting}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            {['Low','Medium','High','Emergency'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {isEdit && (
        <>
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select id="status" name="status" value={formData.status} onChange={handleInputChange} disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {['Open','In Progress','Pending Parts','Scheduled','Resolved','Closed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="assignedToProviderId">Assign Service Provider</Label>
            <select id="assignedToProviderId" name="assignedToProviderId"
              value={formData.assignedToProviderId} onChange={handleInputChange} disabled={submitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select a provider</option>
              {providers.map(p => (
                <option key={p._id} value={p._id}>
                  {p.name}{p.company ? ` · ${p.company}` : ''}
                  {p.categories?.length ? ` (${p.categories.slice(0,2).join(', ')})` : ''}
                </option>
              ))}
              <option value="__manual__">Custom / Manual entry</option>
            </select>
            {(formData.assignedToProviderId === '__manual__' || (!formData.assignedToProviderId && formData.assignedToManual)) && (
              <Input
                name="assignedToManual"
                value={formData.assignedToManual}
                onChange={handleInputChange}
                disabled={submitting}
                placeholder="Name of contractor or staff"
                className="mt-2"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="scheduledDate">Scheduled Date</Label>
            <Input id="scheduledDate" name="scheduledDate" type="date" value={formData.scheduledDate} onChange={handleInputChange} disabled={submitting} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estimatedCost">Estimated Cost ($)</Label>
              <Input id="estimatedCost" name="estimatedCost" type="number" step="0.01" value={formData.estimatedCost} onChange={handleInputChange} disabled={submitting} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="actualCost">Actual Cost ($)</Label>
              <Input id="actualCost" name="actualCost" type="number" step="0.01" value={formData.actualCost} disabled />
            </div>
          </div>
        </>
      )}

      <DialogFooter>
        <Button type="submit" disabled={submitting}>
          {submitting ? `${submitLabel}…` : submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  // ── Loading ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Two-panel layout ─────────────────────────────────────────────
  return (
    <>
      <div className="flex -m-4 md:-m-6 overflow-hidden" style={{ height: 'calc(100vh - 3.5rem)' }}>

        {/* ── Left Panel: Issue List ─────────────────────────────── */}
        <div className={cn(
          'flex flex-col border-r bg-white flex-shrink-0',
          'w-full md:w-80',
          mobilePanel === 'detail' ? 'hidden md:flex' : 'flex',
        )}>
          {/* Header */}
          <div className="flex flex-col gap-3 px-4 py-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-sm flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                Maintenance Requests
              </h2>
              {!isTenant && (
                <Button size="sm" onClick={openAdd} className="h-7 px-2 text-xs gap-1">
                  <Plus className="h-3.5 w-3.5" /> New
                </Button>
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search requests…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm bg-gray-50"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <div className="flex items-center gap-1 flex-1">
                <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 h-7 rounded-md border border-input bg-gray-50 px-2 text-xs"
                >
                  <option value="">All Status</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Pending Parts">Pending Parts</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="h-7 rounded-md border border-input bg-gray-50 px-2 text-xs"
              >
                <option value="">All Priority</option>
                <option value="Emergency">Emergency</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            {filteredRequests.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Request list */}
          <ScrollArea className="flex-1">
            {filteredRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <Wrench className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery || statusFilter || priorityFilter
                    ? 'No requests match your filters.'
                    : 'No maintenance requests yet.'}
                </p>
              </div>
            ) : (
              filteredRequests.map((req) => (
                <RequestCard
                  key={req._id}
                  request={req}
                  isSelected={selectedRequest?._id === req._id}
                  onClick={() => openDetail(req)}
                />
              ))
            )}
          </ScrollArea>
        </div>

        {/* ── Right Panel: Detail ────────────────────────────────── */}
        <div className={cn(
          'flex-1 flex flex-col bg-gray-50/40 min-w-0',
          mobilePanel === 'list' ? 'hidden md:flex' : 'flex',
        )}>

          {/* Mobile back button */}
          <div className="md:hidden flex items-center px-3 py-2 border-b bg-white flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setMobilePanel('list')} className="gap-1.5 text-muted-foreground">
              <ChevronLeft className="h-4 w-4" />
              All Requests
            </Button>
          </div>

          {loadingDetail ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !selectedRequest ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center flex-1 text-center px-6">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center mb-4 shadow-sm">
                <Wrench className="h-8 w-8 text-cyan-500" />
              </div>
              <h3 className="font-semibold text-gray-700 mb-1">Select a request</h3>
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                Choose a maintenance request from the list to view its full details.
              </p>
            </div>
          ) : (
            <ScrollArea className="flex-1">
              <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">

                {/* ── Detail Header ── */}
                <div className="bg-white rounded-xl border p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-gray-900 leading-snug">
                        {selectedRequest.title}
                      </h2>
                      {selectedRequest.caseNumber && (
                        <span className="inline-flex items-center gap-1.5 mt-1 text-xs font-mono font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded px-2 py-0.5">
                          <Hash className="h-3 w-3" />
                          <span className="text-[10px] font-sans font-normal text-cyan-500 uppercase tracking-wide">Case No.</span>
                          {selectedRequest.caseNumber}
                        </span>
                      )}
                    </div>
                    {!isTenant && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => openEdit(selectedRequest)}>
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openDelete(selectedRequest)} className="text-red-600 hover:text-red-700 hover:border-red-300">
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Status + Priority + Category badges */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold', getStatusBadgeColor(selectedRequest.status))}>
                      {getStatusIcon(selectedRequest.status, 'h-3.5 w-3.5')}
                      {selectedRequest.status}
                    </span>
                    <span className={cn('inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold', getPriorityColor(selectedRequest.priority))}>
                      {selectedRequest.priority} Priority
                    </span>
                    <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold bg-gray-50 text-gray-700 border-gray-200">
                      {selectedRequest.category}
                    </span>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DetailRow icon={Building} label="Property" value={selectedRequest.property?.name} />
                    {selectedRequest.unitNumber && <DetailRow icon={Hash}     label="Unit"      value={`Unit ${selectedRequest.unitNumber}`} />}
                    {selectedRequest.tenant     && <DetailRow icon={User}     label="Tenant"    value={selectedRequest.tenant.name} />}
                    {selectedRequest.scheduledDate && (
                      <DetailRow icon={Calendar} label="Scheduled"
                        value={new Date(selectedRequest.scheduledDate).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      />
                    )}
                    <DetailRow icon={Clock} label="Submitted"
                      value={new Date(selectedRequest.createdAt).toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' })}
                    />
                  </div>
                </div>

                {/* ── Service Provider ── */}
                {(selectedRequest.assignedTo?.name || !isTenant) && (
                  <div className="bg-white rounded-xl border px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-shrink-0">Provider</span>
                      {selectedRequest.assignedTo?.name ? (
                        <div className="flex-1 min-w-0">
                          <ProviderRow assignedTo={selectedRequest.assignedTo} />
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground flex-1">Not assigned</span>
                      )}
                      {!isTenant && !isAssignOpen && (
                        <Button variant="outline" size="sm" className="h-6 text-xs px-2 flex-shrink-0"
                          onClick={() => { setAssignProviderId(''); setIsAssignOpen(true); }}>
                          {selectedRequest.assignedTo?.name ? 'Change' : 'Assign'}
                        </Button>
                      )}
                    </div>

                    {!isTenant && isAssignOpen && (
                      <div className="flex gap-2 items-center flex-wrap mt-2 pt-2 border-t">
                        <select
                          value={assignProviderId}
                          onChange={e => setAssignProviderId(e.target.value)}
                          className="flex-1 min-w-0 h-8 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="">Select a provider…</option>
                          {providers.map(p => (
                            <option key={p._id} value={p._id}>
                              {p.name}{p.company ? ` · ${p.company}` : ''}
                              {p.categories?.length ? ` (${p.categories.slice(0,2).join(', ')})` : ''}
                            </option>
                          ))}
                        </select>
                        <Button size="sm" className="h-8 text-xs flex-shrink-0"
                          onClick={handleAssignProvider}
                          disabled={!assignProviderId || submitting}>
                          {submitting ? 'Saving…' : 'Assign'}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 text-xs flex-shrink-0"
                          onClick={() => setIsAssignOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Photo Attachments ── */}
                {(() => {
                  const images = (selectedRequest.attachments || []).filter(a =>
                    a.fileType === 'image' || /\.(jpg|jpeg|png|webp|gif)$/i.test(a.fileUrl || '')
                  );
                  if (!images.length) return null;
                  return (
                    <div className="bg-white rounded-xl border p-5 shadow-sm">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        Photos
                        <span className="text-xs text-muted-foreground font-normal">({images.length})</span>
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => { setLightboxUrl(img.fileUrl); setLightboxIndex(i); }}
                            className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-200 hover:border-cyan-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 flex-shrink-0 group"
                          >
                            <img
                              src={img.fileUrl}
                              alt={img.fileName || `Photo ${i + 1}`}
                              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Issue Details ── */}
                <div className="bg-white rounded-xl border p-5 shadow-sm space-y-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    Issue Details
                  </h3>

                  {/* Raw reported description */}
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Reported Issue</p>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {selectedRequest.description}
                    </p>
                  </div>

                  {/* Niva AI Summary — shown to all roles */}
                  {selectedRequest.aiSummary && (
                    <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Bot className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
                          {isTenant ? 'Summary of Your Report' : 'Niva Conversation Summary'}
                        </p>
                        <span className="ml-auto text-[9px] font-normal text-violet-500 bg-violet-100 border border-violet-200 rounded-full px-1.5 py-0.5">
                          Niva AI
                        </span>
                      </div>
                      {isTenant && (
                        <p className="text-[11px] text-violet-400 italic mb-1.5">
                          This is Niva's understanding of the issue you reported.
                        </p>
                      )}
                      <p className="text-sm text-violet-900 leading-relaxed">
                        {selectedRequest.aiSummary}
                      </p>
                    </div>
                  )}

                  {/* Photo AI analysis */}
                  {selectedRequest.aiAssessment?.detectedIssue && (
                    <div className="rounded-lg border border-cyan-100 bg-cyan-50/60 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-600 mb-1.5 flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5" />
                        Photo Analysis
                      </p>
                      <p className="text-sm text-cyan-800 leading-relaxed">
                        {selectedRequest.aiAssessment.detectedIssue}
                      </p>
                    </div>
                  )}
                </div>

                {/* ── Cost Tracking (landlord only) ── */}
                {!isTenant && (
                  <div className="bg-white rounded-xl border px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-4 mb-3">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-1.5 text-sm">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        Cost Tracking
                      </h3>
                      <div className="flex items-center gap-3 ml-auto">
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">Estimated</p>
                          <p className="text-sm font-bold text-gray-900">${selectedRequest.estimatedCost?.toFixed(2) || '0.00'}</p>
                        </div>
                        <div className="w-px h-8 bg-gray-200" />
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">Actual</p>
                          <p className="text-sm font-bold text-gray-900">${selectedRequest.actualCost?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    </div>

                    {selectedRequest.costEntries?.length > 0 && (
                      <div className="space-y-1 mb-3">
                        {selectedRequest.costEntries.map((entry, i) => (
                          <div key={i} className="flex justify-between items-center bg-gray-50 rounded px-3 py-1.5 text-xs">
                            <div>
                              <span className="font-medium text-gray-800">{entry.description || entry.category}</span>
                              <span className="text-muted-foreground ml-2">
                                {new Date(entry.date).toLocaleDateString()} · {entry.addedBy?.name}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900">${entry.amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={costData.description}
                        onChange={(e) => setCostData({ ...costData, description: e.target.value })}
                        disabled={submitting}
                        className="flex-1 h-7 text-xs"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Amount"
                        value={costData.amount}
                        onChange={(e) => setCostData({ ...costData, amount: e.target.value })}
                        disabled={submitting}
                        className="w-24 h-7 text-xs"
                      />
                      <Button size="sm" onClick={handleAddCost} disabled={submitting || !costData.amount} className="h-7 text-xs px-2">
                        Add
                      </Button>
                    </div>
                  </div>
                )}

                {/* ── AI Triage Assessment (landlord only) ── */}
                {!isTenant && (() => {
                  const triageComment = selectedRequest.comments?.find(
                    c => c.addedBy?.name === 'Niva AI Triage'
                  );
                  if (!triageComment) return null;
                  return (
                    <div className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-5 shadow-sm">
                      <h3 className="font-semibold text-cyan-800 mb-3 flex items-center gap-2">
                        <BrainCircuit className="h-4 w-4 text-cyan-600" />
                        AI Triage Assessment
                        <span className="ml-auto text-[10px] font-normal text-cyan-600 bg-cyan-100 border border-cyan-200 rounded-full px-2 py-0.5">
                          Owner only
                        </span>
                      </h3>
                      <div className="space-y-1.5">
                        {triageComment.text.split('\n').filter(Boolean).slice(1).map((line, i) => {
                          const [label, ...rest] = line.split(':');
                          const value = rest.join(':').trim();
                          if (!value) return (
                            <p key={i} className="text-sm text-cyan-700 leading-relaxed mt-2">{line}</p>
                          );
                          return (
                            <div key={i} className="flex gap-2 text-sm">
                              <span className="font-semibold text-cyan-800 min-w-[130px] flex-shrink-0">{label.trim()}:</span>
                              <span className="text-cyan-700">{value}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-[10px] text-cyan-500">
                          Generated {new Date(triageComment.addedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {selectedRequest.threadId && (
                          <button
                            onClick={handleLoadConversation}
                            className="flex items-center gap-1 text-[11px] text-cyan-600 hover:text-cyan-800 hover:underline"
                          >
                            <Bot className="h-3.5 w-3.5" />
                            View AI Conversation
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Inline Approval (landlord, Pending Approval cases) ── */}
                {!isTenant && selectedRequest.status === 'Pending Approval' && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                    <h3 className="font-semibold text-amber-800 mb-1 flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      Owner Approval Required
                    </h3>
                    {approvalResult ? (
                      <div className="flex items-center gap-2 mt-2">
                        {approvalResult === 'approve'
                          ? <CheckCircle className="h-5 w-5 text-green-500" />
                          : <XCircle    className="h-5 w-5 text-red-500" />}
                        <span className="text-sm font-medium text-gray-800">
                          Request {approvalResult === 'approve' ? 'approved' : 'rejected'} successfully.
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-3 mt-3">
                        <p className="text-xs text-amber-700">
                          A reason is required when rejecting. Notes are optional for approval.
                        </p>
                        <textarea
                          rows={2}
                          value={approvalReason}
                          onChange={(e) => setApprovalReason(e.target.value)}
                          placeholder="Reason / notes (required if rejecting)..."
                          className="w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                        />
                        {approvalError && (
                          <p className="text-xs text-red-600">{approvalError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleInlineApproval('approve')}
                            disabled={submitting}
                            className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                          >
                            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleInlineApproval('reject')}
                            disabled={submitting}
                            variant="destructive"
                            className="h-8 text-xs"
                          >
                            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                            Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Activity & Comments ── */}
                <div className="bg-white rounded-xl border p-5 shadow-sm">
                  {/* Header with counts */}
                  {(() => {
                    const allVisible = (selectedRequest.comments || []).filter(c => c.addedBy?.name !== 'Niva AI Triage');
                    const userComments = allVisible.filter(c => c.addedBy?.userType !== 'Activity');
                    const activityEntries = allVisible.filter(c => c.addedBy?.userType === 'Activity');
                    return (
                      <>
                        <div className="flex items-center gap-4 mb-4">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-muted-foreground" />
                            Comments
                            {userComments.length > 0 && (
                              <span className="text-xs text-muted-foreground font-normal">({userComments.length})</span>
                            )}
                          </h3>
                          {activityEntries.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                              <Activity className="h-3.5 w-3.5" />
                              {activityEntries.length} activity event{activityEntries.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {allVisible.length === 0 && (
                          <p className="text-sm text-muted-foreground mb-4">No activity yet.</p>
                        )}

                        <div className="space-y-1 mb-4">
                          {allVisible.map((comment, i) => {
                            const isActivity = comment.addedBy?.userType === 'Activity';
                            if (isActivity) {
                              return (
                                <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-md text-xs text-muted-foreground group">
                                  <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <Activity className="h-3 w-3 text-gray-400" />
                                  </div>
                                  <span className="flex-1 text-gray-500">{comment.text}</span>
                                  <span className="text-[10px] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {new Date(comment.addedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    {comment.addedBy?.name && comment.addedBy.name !== 'System' && ` · ${comment.addedBy.name}`}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <div key={i} className="flex gap-3 pt-1">
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-600">
                                  {(comment.addedBy?.name || 'U').charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-semibold text-gray-700">{comment.addedBy?.name || 'Unknown'}</span>
                                    <span className="text-[10px] text-muted-foreground">
                                      {new Date(comment.addedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600">{comment.text}</p>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={commentsEndRef} />
                        </div>
                      </>
                    );
                  })()}

                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a comment…"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                      disabled={submitting}
                      className="flex-1 h-9 text-sm"
                    />
                    <Button size="sm" onClick={handleAddComment} disabled={submitting || !commentText.trim()} className="h-9">
                      Post
                    </Button>
                  </div>
                </div>

              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* ── Add Modal ──────────────────────────────────────────────── */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Maintenance Request</DialogTitle></DialogHeader>
          {renderMaintenanceForm(handleSubmitAdd, 'Create Request')}
        </DialogContent>
      </Dialog>

      {/* ── Edit Modal ─────────────────────────────────────────────── */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Maintenance Request</DialogTitle></DialogHeader>
          {renderMaintenanceForm(handleSubmitEdit, 'Update Request', true)}
        </DialogContent>
      </Dialog>

      {/* ── Delete Modal ───────────────────────────────────────────── */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader><DialogTitle>Delete Maintenance Request</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{modalTarget?.title}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={submitting}>
              {submitting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Photo Lightbox ─────────────────────────────────────────── */}
      {lightboxUrl && (() => {
        const images = (selectedRequest?.attachments || []).filter(a =>
          a.fileType === 'image' || /\.(jpg|jpeg|png|webp|gif)$/i.test(a.fileUrl || '')
        );
        const prev = () => {
          const i = (lightboxIndex - 1 + images.length) % images.length;
          setLightboxIndex(i);
          setLightboxUrl(images[i].fileUrl);
        };
        const next = () => {
          const i = (lightboxIndex + 1) % images.length;
          setLightboxIndex(i);
          setLightboxUrl(images[i].fileUrl);
        };
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white"
              onClick={() => setLightboxUrl(null)}
            >
              <X className="h-7 w-7" />
            </button>
            {images.length > 1 && (
              <>
                <button
                  className="absolute left-4 text-white/70 hover:text-white p-2"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  className="absolute right-4 text-white/70 hover:text-white p-2"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}
            <img
              src={lightboxUrl}
              alt="Attachment"
              className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {images.length > 1 && (
              <p className="absolute bottom-4 text-white/60 text-sm">
                {lightboxIndex + 1} / {images.length}
              </p>
            )}
          </div>
        );
      })()}

      {/* ── AI Conversation Dialog (owner only) ────────────────────── */}
      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent className="sm:max-w-[580px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-cyan-600" />
              AI Intake Conversation
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-1">
            {convLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : convMessages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No conversation history available.</p>
            ) : (
              <div className="space-y-3 py-2">
                {convMessages.map((msg, i) => (
                  <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role === 'assistant' && (
                      <div className="h-7 w-7 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="h-3.5 w-3.5 text-cyan-600" />
                      </div>
                    )}
                    <div className={cn(
                      'max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                      msg.role === 'user'
                        ? 'bg-cyan-600 text-white rounded-br-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    )}>
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-gray-600">
                        {(selectedRequest?.tenant?.name || 'T').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Maintenance;
